/*
 * Copyright (c) 2020
 * Company: G-Portal
 * Filename: actions.ts
 * Class: actions.ts
 * Author: Karl Magerl<karl@g-portal.com>
 * Version:
 */

import { ActionTree } from 'vuex';
import { AxiosError, AxiosResponse } from 'axios';
import {
  CloudEditRequest,
  CloudEditResponse,
  CloudPriceRequest,
  CloudPriceResponse,
  DashboardState,
  GetServerRequest,
  GetServerResponse, UpdateServerRequest,
} from './types';
import { RootState } from '../types';
import { ApiEndpointEnum, getApiRequestConfig } from '../../config/network';

export const actions: ActionTree<DashboardState, RootState> = {
  getGamecloudForEdit({ commit }, orderId: number): Promise<CloudEditResponse> {
    commit('gamecloudEditLoading');

    const requestConfig = getApiRequestConfig(ApiEndpointEnum.Gamecloud);
    const formData = new FormData();
    formData.append('orderId', orderId.toString());

    return new Promise<CloudEditResponse>(((resolve, reject) => {
      this.state.$http.post('getForEdit', formData, requestConfig)
        .then((response) => {
          const payload = response && response.data;
          commit('gamecloudEditLoaded', payload);

          resolve(payload);
        }, (error) => {
          commit('gamecloudEditError');

          reject();
        });
    }));
  },
  getCloudPrice({ commit }, request: CloudPriceRequest) {
    commit('cloudPriceLoading');

    return new Promise<CloudPriceResponse>((resolve, reject) => {
      this.state.$http.get(`calculateCloudPrice/${request.orderId}/${request.units}/${request.durationId}`, getApiRequestConfig(ApiEndpointEnum.Gamecloud))
        .then((response) => {
          const payload = response && response.data;
          commit('cloudPriceLoaded', payload);

          resolve(payload);
        }, (error) => {
          commit('cloudPriceError');

          reject();
        });
    });
  },
  editOrder({ commit }, request: CloudEditRequest): Promise<void> {
    commit('editOrderLoading');

    const formData = new FormData();
    formData.append('cloud-order-id', request.orderId.toString());
    formData.append('duration-id', request.durationId.toString());
    formData.append('slots', request.units.toString());

    return new Promise<void>((resolve, reject) => {
      this.state.$http.post('editOrder', formData, getApiRequestConfig(ApiEndpointEnum.Gamecloud))
        .then((response) => {
          const payload = response && response.data;
          commit('editOrderLoaded', payload);

          resolve();
        }, (error: AxiosError) => {
          switch (error.response.status) {
            // customer doesnt have enough balance
            case 401:
              commit('editOrderError', error.response.data);
              reject(error.response.data);
              break;

            // generic error handling
            default:
              commit('editOrderError', null);
              // eslint-disable-next-line prefer-promise-reject-errors
              reject(null);
          }
        });
    });
  },
  getServer({ commit }, request: GetServerRequest): Promise<GetServerResponse> {
    commit('getServerLoading');

    return new Promise<GetServerResponse>((resolve, reject) => {
      this.state.$http.get(`editServer/${encodeURI(request.orderId.toString())}`, getApiRequestConfig(ApiEndpointEnum.Gamecloud))
        .then((response: AxiosResponse<GetServerResponse>) => {
          const payload = response && response.data;
          commit('getServerLoaded', payload);

          resolve(payload);
        }, (error: AxiosError) => {
          commit('getServerError', error.response.data && error.response.data.message);

          reject(error);
        });
    });
  },
  updateServer({ commit }, request: UpdateServerRequest): Promise<void> {
    commit('updateServerLoading');

    return new Promise<void>((resolve, reject) => {
      this.state.$http.put(`editServer/${encodeURI(request.orderId.toString())}`, request, getApiRequestConfig(ApiEndpointEnum.Gamecloud))
        .then(() => {
          commit('updateServerSuccess');
          resolve();
        }, (error: AxiosError) => {
          commit('updateServerError', error.response.data && error.response.data.message);

          reject(error);
        });
    });
  },
};
